import { useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import '../q3/q3.css';

interface PreviewRow {
  empId: string;
  rm: string;
  head: string;
  loc: string;
  cohort: string | null;
  coreTotal: number;
  cat2Total: number;
  pct: number;
  qual: string;
}

interface UploadResponse {
  ok: boolean;
  dryRun?: boolean;
  rowCount: number;
  preview: PreviewRow[];
  warnings: string[];
}

interface UploadError {
  error: string;
  message?: string;
  details?: unknown;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'previewing'; result: UploadResponse; file: File }
  | { kind: 'uploading' }
  | { kind: 'done'; result: UploadResponse }
  | { kind: 'error'; message: string };

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as number[]);
  }
  return btoa(binary);
}

async function callUpload(
  password: string,
  file: File,
  dryRun: boolean,
): Promise<UploadResponse> {
  const dataBase64 = await fileToBase64(file);
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Password': password,
    },
    body: JSON.stringify({ filename: file.name, dataBase64, dryRun }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as UploadError;
    throw new Error(err.message || err.error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<UploadResponse>;
}

export default function UploadPage({ onSignOut }: { onSignOut: () => void }) {
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [dragging, setDragging] = useState(false);

  const onFilePicked = async (file: File) => {
    if (!password) {
      setStatus({ kind: 'error', message: 'Enter the admin password first.' });
      return;
    }
    setStatus({ kind: 'uploading' });
    try {
      const result = await callUpload(password, file, true);
      setStatus({ kind: 'previewing', result, file });
    } catch (e) {
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : 'Unknown error' });
    }
  };

  const onConfirmUpload = async () => {
    if (status.kind !== 'previewing') return;
    const { file } = status;
    setStatus({ kind: 'uploading' });
    try {
      const result = await callUpload(password, file, false);
      setStatus({ kind: 'done', result });
    } catch (e) {
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : 'Unknown error' });
    }
  };

  const onFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void onFilePicked(f);
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void onFilePicked(f);
  };

  const reset = () => setStatus({ kind: 'idle' });

  return (
    <div className="sk sk-upload" data-view="home">
      <div className="sk-wrap">
        <div className="sk-top">
          <a className="sk-brandrow" href="/" aria-label="Back to the leaderboard">
            <div className="sk-filogo" role="img" aria-label="FundsIndia Private Wealth" />
            <div className="sk-vr" />
            <div className="sk-toplogo sk-logo" role="img" aria-label="Shakti · Powering to 25K" />
          </a>
          <div className="sk-topacts">
            <a className="sk-back" href="/">
              ← Leaderboard
            </a>
            <button className="sk-signout" onClick={onSignOut} title="Sign out" aria-label="Sign out">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
    <main className="upload-shell">
      <div className="page-head">
        <div>
          <div className="page-title">Upload data</div>
          <div className="page-desc">
            Replace the current Shakti Q3 dataset. CSV or XLSX · max 10MB · overwrites all rows from row 3 onwards.
          </div>
        </div>
      </div>

      <form onSubmit={(e: FormEvent) => e.preventDefault()} className="upload-form">
        <div>
          <label htmlFor="pwd" className="field-label">Admin password</label>
          <input
            id="pwd"
            type="password"
            className="input-text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••••••"
            autoComplete="off"
          />
        </div>

        <label
          className={'dropzone' + (dragging ? ' dragging' : '')}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={onFileInput}
            style={{ display: 'none' }}
          />
          <div className="dropzone-title">Drop a CSV or XLSX here, or click to browse</div>
          <div className="dropzone-sub">
            Needs EMP.ID · RM_Names · Reporting P&amp;L Head · Location · COHORT · Core &amp; Mandatory totals · Max 10 MB
          </div>
        </label>
      </form>

      {status.kind === 'uploading' && (
        <div style={{ marginTop: 40, color: 'var(--ink-3)', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          · Parsing ·
        </div>
      )}

      {status.kind === 'error' && (
        <div className="upload-error">
          <b>Error</b> — {status.message}
          <div style={{ marginTop: 10 }}>
            <button className="btn-ghost" style={{ padding: 0 }} onClick={reset}>Try again</button>
          </div>
        </div>
      )}

      {status.kind === 'previewing' && (
        <section style={{ marginTop: 40 }}>
          <div className="upload-preview-ban">
            <span className="num tnum">{status.result.rowCount}</span>
            <span className="label">rows ready · first 5 shown</span>
          </div>

          <div className="table-card" style={{ marginBottom: 24 }}>
            <div className="table-scroll">
              <table className="lb">
                <thead>
                  <tr>
                    <th>EMP.ID</th>
                    <th>RM</th>
                    <th>Head</th>
                    <th>Location</th>
                    <th>Cohort</th>
                    <th className="right">Core ₹ Cr</th>
                    <th className="right">Mandatory ₹ Cr</th>
                    <th className="right">% of target</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {status.result.preview.map((r) => (
                    <tr key={r.empId}>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{r.empId}</td>
                      <td>{r.rm}</td>
                      <td style={{ color: 'var(--ink-3)' }}>{r.head}</td>
                      <td style={{ color: 'var(--ink-3)' }}>{r.loc}</td>
                      <td style={{ color: r.cohort ? 'var(--ink-3)' : 'var(--danger, #c4442a)' }}>{r.cohort ?? 'No cohort'}</td>
                      <td className="num">{r.coreTotal.toFixed(2)}</td>
                      <td className="num">{r.cat2Total.toFixed(2)}</td>
                      <td className="num" style={{ color: 'var(--accent)' }}>{(r.pct * 100).toFixed(1)}%</td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: r.qual === 'Qualified' ? 'var(--success)' : 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.qual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {status.result.warnings.length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 16, color: 'var(--ink-3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
              {status.result.warnings.length} warning(s):
              <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                {status.result.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button className="btn-primary" onClick={onConfirmUpload}>
              Confirm · overwrite {status.result.rowCount} rows →
            </button>
            <button className="btn-ghost" onClick={reset}>Cancel</button>
          </div>
        </section>
      )}

      {status.kind === 'done' && (
        <div className="upload-success">
          <div className="upload-success-headline">
            <span className="num tnum">{status.result.rowCount}</span>
            <span className="unit">rows written</span>
          </div>
          <div className="upload-success-sub">Sheet updated. Leaderboard reflects new data within 60s (CDN cache).</div>
          <div style={{ marginTop: 16, display: 'flex', gap: 4 }}>
            <a href="/" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
              View leaderboard →
            </a>
            <button className="btn-ghost" onClick={reset}>Upload another</button>
          </div>
        </div>
      )}
    </main>
      </div>
    </div>
  );
}
